// SPDX-License-Identifier: AGPL-3.0-only
// Copyright (C) 2025 realvirtual GmbH <https://realvirtual.io>

/** Floating CONNECT historian trend viewer with range selection and live follow. */

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  LinearProgress,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Refresh, PlayArrow, Pause, StackedLineChart, ViewAgenda } from '@mui/icons-material';
import { FloatingPanel } from './FloatingPanel';
import { useEChart } from '../../hooks/use-echart';
import { buildHistorianChartOption } from './historian-chart-options';
import {
  historianStore,
  type HistorianTimeRange,
} from './historian-store';

const DEFAULT_WIDTH = 840;
const DEFAULT_HEIGHT = 520;

const RANGE_MS: Record<Exclude<HistorianTimeRange, 'custom'>, number> = {
  '1h': 60 * 60 * 1000,
  '6h': 6 * 60 * 60 * 1000,
  '24h': 24 * 60 * 60 * 1000,
  '7d': 7 * 24 * 60 * 60 * 1000,
};

function localDateTimeValue(date: Date): string {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 16);
}

export interface HistorianTrendPanelProps {
  open: boolean;
  onClose: () => void;
}

export function HistorianTrendPanel({ open, onClose }: HistorianTrendPanelProps) {
  const snapshot = useSyncExternalStore(
    historianStore.subscribe,
    historianStore.getSnapshot,
    historianStore.getSnapshot,
  );
  const [customFrom, setCustomFrom] = useState(() => localDateTimeValue(new Date(Date.now() - RANGE_MS['1h'])));
  const [customTo, setCustomTo] = useState(() => localDateTimeValue(new Date()));
  const { containerRef, chartInstance, isReady } = useEChart({ open });

  const series = useMemo(() => [...snapshot.results.values()], [snapshot.results]);
  const signalNames = useMemo(() => snapshot.signals.map((signal) => signal.name), [snapshot.signals]);

  const selectedRange = useCallback((): { from: Date; to: Date } | null => {
    const to = snapshot.timeRange === 'custom' ? new Date(customTo) : new Date();
    const from = snapshot.timeRange === 'custom'
      ? new Date(customFrom)
      : new Date(to.getTime() - RANGE_MS[snapshot.timeRange]);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from >= to) return null;
    return { from, to };
  }, [customFrom, customTo, snapshot.timeRange]);

  const runQuery = useCallback(async (signals = snapshot.selectedSignals) => {
    const range = selectedRange();
    if (signals.length === 0 || !range) return;
    await historianStore.queryHistory(signals, range.from, range.to);
  }, [selectedRange, snapshot.selectedSignals]);

  // Live-follow: with a relative range the window rolls forward automatically
  // so fresh InfluxDB data appears without pressing Refresh. Persisted — off stays off.
  const follow = snapshot.follow;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;   // closing the panel mid-bootstrap must not mutate the store afterwards
    void historianStore.refreshStatus().then(async (status) => {
      if (cancelled || !status.enabled || !status.connected) return;
      const signals = await historianStore.refreshSignals();
      if (cancelled) return;
      // Only seed a DEFAULT selection when nothing is restored — re-setting an
      // unchanged selection would create a new array reference and make the
      // auto-query effect below fire twice per panel open.
      const current = historianStore.getSnapshot().selectedSignals;
      if (current.length === 0) {
        const fallback = signals.filter((signal) => signal.active).slice(0, 1).map((signal) => signal.name);
        if (fallback.length > 0) historianStore.setSelectedSignals(fallback);
      }
      // Querying is owned by the auto-query effect below (fires on selection/status change).
    });
    return () => { cancelled = true; };
    // Opening is the trigger. Store updates produced by this bootstrap must not restart it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Auto-query: any selection or range change while the panel is open (including a
  // REC-dot click in the ConnectPanel adding a signal) reloads after a short debounce.
  useEffect(() => {
    if (!open) return;
    if (!snapshot.status?.enabled || !snapshot.status.connected) return;
    if (snapshot.selectedSignals.length === 0) return;
    const timer = window.setTimeout(() => void runQuery(), 250);
    return () => window.clearTimeout(timer);
    // runQuery already tracks range/custom inputs; status.connected gates 400 spam.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, snapshot.selectedSignals, snapshot.timeRange, customFrom, customTo, snapshot.status?.enabled, snapshot.status?.connected]);

  // Rolling live refresh every 30 s — relative ranges only (a custom range is a
  // frozen window by definition), skipped while a query is already in flight.
  useEffect(() => {
    if (!open || !follow) return;
    if (snapshot.timeRange === 'custom') return;
    if (!snapshot.status?.connected) return;
    const interval = window.setInterval(() => {
      if (!historianStore.getSnapshot().loading) void runQuery();
    }, 30_000);
    return () => window.clearInterval(interval);
  }, [open, follow, snapshot.timeRange, snapshot.status?.connected, runQuery]);

  // Full notMerge ONLY on structural change (signal set or layout switched):
  // a per-tick teardown flashes the canvas on every live-follow refresh, and
  // replaceMerge crashes ECharts' axis tooltip on removed series (getRawIndex).
  const chartStructureRef = useRef('');
  useEffect(() => {
    if (!open || !isReady || !chartInstance.current) return;
    const structure = `${snapshot.layout}|${series.map((entry) => entry.signal).join(' ')}`;
    const structural = structure !== chartStructureRef.current;
    chartStructureRef.current = structure;
    chartInstance.current.setOption(
      buildHistorianChartOption(series, snapshot.layout, snapshot.laneBounds),
      structural ? { notMerge: true, lazyUpdate: true } : { lazyUpdate: true },
    );
  }, [chartInstance, isReady, open, series, snapshot.layout, snapshot.laneBounds]);

  const status = snapshot.status;
  const rangeValid = selectedRange() !== null;
  const statusColor = status?.connected ? 'success' : status?.authError ? 'error' : 'warning';
  const statusLabel = status?.connected ? 'Historian connected' : status?.authError ? 'Authorization failed' : 'Historian unavailable';

  const defaultPosition = useMemo(() => ({
    x: Math.max(80, Math.round(window.innerWidth / 2 - DEFAULT_WIDTH / 2)),
    y: Math.max(24, Math.round(window.innerHeight / 2 - DEFAULT_HEIGHT / 2) - 40),
  }), []);

  return (
    <FloatingPanel
      open={open}
      onClose={onClose}
      title="Historian Trend"
      titleColor="#4fc3f7"
      subtitle="CONNECT · InfluxDB"
      defaultWidth={DEFAULT_WIDTH}
      defaultHeight={DEFAULT_HEIGHT}
      defaultPosition={defaultPosition}
      zIndex={1500}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <Box sx={{ px: 1.5, py: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <Autocomplete
            multiple
            size="small"
            options={signalNames}
            value={snapshot.selectedSignals}
            onChange={(_event, value) => historianStore.setSelectedSignals(value)}
            renderInput={(params) => <TextField {...params} label="Signals" placeholder="Select recorded signals" />}
            sx={{ minWidth: 260, flex: '1 1 320px' }}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={snapshot.timeRange}
            onChange={(_event, value: HistorianTimeRange | null) => { if (value) historianStore.setTimeRange(value); }}
            aria-label="Historian time range"
          >
            {(['1h', '6h', '24h', '7d', 'custom'] as const).map((range) => (
              <ToggleButton key={range} value={range} aria-label={`${range} range`} sx={{ textTransform: 'none' }}>
                {range === 'custom' ? 'Custom' : range}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={snapshot.layout}
            onChange={(_event, value: 'overlay' | 'stacked' | null) => { if (value) historianStore.setLayout(value); }}
            aria-label="Chart layout"
          >
            <ToggleButton value="overlay" aria-label="Overlay — shared axis" sx={{ px: 1 }}>
              <Tooltip title="Overlay — all signals on one shared axis"><StackedLineChart sx={{ fontSize: 15 }} /></Tooltip>
            </ToggleButton>
            <ToggleButton value="stacked" aria-label="Stacked — one lane per signal" sx={{ px: 1 }}>
              <Tooltip title="Stacked — one lane per signal (logic-analyzer view)"><ViewAgenda sx={{ fontSize: 15 }} /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title={follow ? 'Live follow is on — the window rolls forward every 30 s (relative ranges). Click to pause.' : 'Live follow is paused — click to resume automatic reload.'}>
            <span>
              <ToggleButton
                size="small"
                value="follow"
                selected={follow && snapshot.timeRange !== 'custom'}
                disabled={snapshot.timeRange === 'custom'}
                onChange={() => historianStore.setFollow(!follow)}
                aria-label={follow ? 'Pause live follow' : 'Resume live follow'}
                sx={{ textTransform: 'none', px: 1 }}
              >
                {follow ? <Pause sx={{ fontSize: 14, mr: 0.5 }} /> : <PlayArrow sx={{ fontSize: 14, mr: 0.5 }} />}
                Live
              </ToggleButton>
            </span>
          </Tooltip>
          {/* Live follow makes a Refresh button redundant — it only appears when the
              window is frozen (follow paused or a custom range). Static icon: the
              loading state lives in the thin progress line over the chart, never
              as an icon swap that makes the toolbar flicker on every tick. */}
          {(!follow || snapshot.timeRange === 'custom') && (
            <Tooltip title="Query the selected signals and time range">
              <span>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<Refresh />}
                  disabled={snapshot.loading || snapshot.selectedSignals.length === 0 || !rangeValid || !status?.connected}
                  onClick={() => void runQuery()}
                >
                  Refresh
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        {snapshot.timeRange === 'custom' && (
          <Box sx={{ px: 1.5, py: 0.75, display: 'flex', gap: 1, alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <TextField
              size="small"
              type="datetime-local"
              label="From"
              value={customFrom}
              onChange={(event) => setCustomFrom(event.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: customTo } }}
            />
            <TextField
              size="small"
              type="datetime-local"
              label="To"
              value={customTo}
              onChange={(event) => setCustomTo(event.target.value)}
              slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: customFrom } }}
            />
            {!rangeValid && <Typography color="error" sx={{ fontSize: 11 }}>From must be before To.</Typography>}
          </Box>
        )}

        {snapshot.error && <Alert severity="error" sx={{ borderRadius: 0 }}>{snapshot.error}</Alert>}

        <Box sx={{ position: 'relative', flex: 1, minHeight: 220 }}>
          {/* Calm loading signal: a 2px line instead of spinner/icon swaps. */}
          {snapshot.loading && (
            <LinearProgress
              aria-label="Loading historian data"
              sx={{
                position: 'absolute', top: 0, left: 0, right: 0, height: 2, zIndex: 1,
                bgcolor: 'transparent', '& .MuiLinearProgress-bar': { bgcolor: '#4fc3f7' },
                '@media (prefers-reduced-motion: reduce)': { display: 'none' },
              }}
            />
          )}
          <Box ref={containerRef} sx={{ position: 'absolute', inset: 0 }} />
          {!snapshot.loading && series.length === 0 && (
            <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, textAlign: 'center', maxWidth: 360 }}>
                Select one or more historian signals, then refresh the current time range.
              </Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ px: 1.5, py: 0.75, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <Chip size="small" color={statusColor} variant="outlined" label={statusLabel} />
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', fontSize: 10 }}>
            Dropped: {status?.droppedPoints ?? 0}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontFamily: 'monospace', fontSize: 10, flex: 1 }}>
            Last write: {status?.lastWriteUtc ? new Date(status.lastWriteUtc).toLocaleString() : '—'}
          </Typography>
        </Box>
      </Box>
    </FloatingPanel>
  );
}
